import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const AuthContext = createContext();

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// İnaktivite süresi: 1 saat (milisaniye cinsinden)
const INACTIVITY_TIMEOUT = 60 * 60 * 1000; // 1 saat
const WARNING_BEFORE_LOGOUT = 5 * 60 * 1000; // 5 dakika önce uyarı

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const inactivityTimerRef = useRef(null);
  const warningTimerRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  // Aktivite sıfırlama fonksiyonu
  const resetInactivityTimer = useCallback(() => {
    lastActivityRef.current = Date.now();
    
    // Mevcut timer'ları temizle
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
    }
    
    // Sadece giriş yapmış kullanıcılar için timer başlat
    const token = localStorage.getItem('access_token');
    if (!token) return;
    
    // Uyarı timer'ı (logout'tan 5 dk önce)
    warningTimerRef.current = setTimeout(() => {
      toast.warning('5 dakika içinde oturumunuz sonlanacak. Aktif kalmak için sayfayı kullanın.', {
        duration: 10000,
        id: 'session-warning'
      });
    }, INACTIVITY_TIMEOUT - WARNING_BEFORE_LOGOUT);
    
    // Logout timer'ı
    inactivityTimerRef.current = setTimeout(() => {
      toast.error('Oturumunuz zaman aşımına uğradı. Lütfen tekrar giriş yapın.', {
        duration: 5000
      });
      performLogout();
    }, INACTIVITY_TIMEOUT);
  }, []);

  // Logout işlemi
  const performLogout = useCallback(() => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('last_activity');
    setUser(null);
    
    // Timer'ları temizle
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
    }
  }, []);

  // Kullanıcı aktivitelerini dinle
  useEffect(() => {
    const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    
    const handleActivity = () => {
      if (user) {
        resetInactivityTimer();
      }
    };
    
    activityEvents.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true });
    });
    
    return () => {
      activityEvents.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
    };
  }, [user, resetInactivityTimer]);

  // User değiştiğinde timer'ı başlat/durdur
  useEffect(() => {
    if (user) {
      resetInactivityTimer();
    } else {
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
      if (warningTimerRef.current) {
        clearTimeout(warningTimerRef.current);
      }
    }
  }, [user, resetInactivityTimer]);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('access_token');
    if (token) {
      try {
        const response = await axios.get(`${API_URL}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUser(response.data);
      } catch (error) {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
      }
    }
    setLoading(false);
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/auth/login`, { email, password });
    const data = response.data;
    
    // Check if 2FA is required
    if (data.needs_verification) {
      // Return the response for 2FA handling
      return data;
    }
    
    // Normal login - save tokens
    const { access_token, refresh_token, user: userData } = data;
    localStorage.setItem('access_token', access_token);
    localStorage.setItem('refresh_token', refresh_token);
    setUser(userData);
    
    // İnaktivite timer'ını başlat
    resetInactivityTimer();
    
    // Post-login background tasks
    runBackgroundTasks(access_token);
    
    return data;
  };

  const loginWith2FA = async (email, code, verificationId) => {
    const response = await axios.post(`${API_URL}/auth/verify-code`, {
      email,
      code,
      verification_id: verificationId
    });
    
    const { access_token, refresh_token, user: userData } = response.data;
    localStorage.setItem('access_token', access_token);
    localStorage.setItem('refresh_token', refresh_token);
    setUser(userData);
    
    // İnaktivite timer'ını başlat
    resetInactivityTimer();
    
    // Post-login background tasks
    runBackgroundTasks(access_token);
    
    return response.data;
  };

  const runBackgroundTasks = (token) => {
    try {
      // Update exchange rates
      axios.post(`${API_URL}/exchange-rates/refresh`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(() => {
        console.log('Döviz kurları güncellendi');
      }).catch(err => {
        console.log('Kur güncelleme hatası:', err.message);
      });
      
      // Check anchorage emails
      axios.post(`${API_URL}/anchorage/sync-emails`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(res => {
        if (res.data.new_entries > 0) {
          console.log(`${res.data.new_entries} yeni demirsahası kaydı eklendi`);
        } else {
          console.log('Demirsahası kontrol edildi');
        }
      }).catch(err => {
        console.log('Mail kontrolü hatası:', err.message);
      });
    } catch (err) {
      console.log('Arka plan görevleri hatası:', err.message);
    }
  };

  const logout = () => {
    performLogout();
  };

  const register = async (userData) => {
    const token = localStorage.getItem('access_token');
    const response = await axios.post(`${API_URL}/auth/register`, userData, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, loginWith2FA, logout, register }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
